import { Chart } from 'chart.js';
import { CandleData } from 'client/voladeum/candles/types';
import { FORMAT_TYPE } from 'cyprus/enums';
import { formatValue } from 'cyprus/formats';

import { BORDER } from '../../constants';

import { ChartPrice, PriceOptions, PricePlugin } from './types';
import { drawChip, drawLine } from './utils';

function getLatestPrices(data: CandleData[]): { current: number; previous: number } | null {
  if (data.length < 2) {
    return null;
  }
  return {
    current: data[data.length - 1].c,
    previous: data[data.length - 2].c,
  };
}

function getPriceScale(scales: Chart['scales']) {
  return Object.values(scales).find(
    (scale) => scale.id.includes('price') || scale.id.includes('y')
  );
}

export const pricePlugin: PricePlugin = {
  id: 'price',
  defaults: { enabled: false },

  afterInit(chart: ChartPrice) {
    chart.price = { current: null, previous: null, y: null };
  },

  afterUpdate(chart: ChartPrice, _args: unknown, options: PriceOptions) {
    if (!options?.enabled) return;

    const data = chart.data.datasets[0].data as CandleData[];
    const prices = getLatestPrices(data);
    const scales = getPriceScale(chart.scales);

    // Reset the chart.price values when prices are null
    if (!prices || !scales) {
      chart.price = { current: null, previous: null, y: null };
      return;
    }

    const { current, previous } = prices;
    const y = scales.getPixelForValue(current);

    if (!chart.chartArea || y < chart.chartArea.top || y > chart.chartArea.bottom) {
      chart.price = { current: null, previous: null, y: null };
      return;
    }

    chart.price = { current, previous, y };
  },

  afterDraw(chart: ChartPrice, _args: unknown, options: PriceOptions) {
    if (!options?.enabled) return;

    const { ctx, chartArea } = chart;
    if (!chartArea) return;

    const { current, previous, y } = chart.price;
    if (current === null || previous === null || y === null) return;

    const { right, top, bottom } = chartArea;
    if (y < top || y > bottom) return;

    const bullish = current >= previous;
    const color = bullish ? BORDER.GREEN : BORDER.RED;

    ctx.save();

    // dotted line
    drawLine(ctx, 0, y, right, y, color);

    // price chip
    const price = formatValue(current, { format: FORMAT_TYPE.CURRENCY });
    drawChip(ctx, price, right, y, false, { background: color, border: color });
    ctx.restore();
  },
};
